/**
 * Coffee Bean Clicker v2 - Achievement Panel
 * Displays achievements with category filter chips
 */

import * as PIXI from 'pixi.js';
import type { GameState } from '../../game/index.js';
import { ACHIEVEMENTS, type AchievementDef } from '../../game/data/achievements.js';
import { t } from '../../i18n/index.js';
import { THEME, LAYOUT } from './theme.js';
import { drawGlassCard, makeText } from './UIHelpers.js';
import { ScrollContainer } from './ScrollContainer.js';

type AchCategory = 'all' | AchievementDef['category'];

const CATEGORIES: { key: AchCategory; labelKey: string }[] = [
  { key: 'all', labelKey: 'tab.achievements' },
  { key: 'tapping', labelKey: 'ach.cat.tapping' },
  { key: 'production', labelKey: 'ach.cat.production' },
  { key: 'producers', labelKey: 'ach.cat.producers' },
  { key: 'prestige', labelKey: 'ach.cat.prestige' },
  { key: 'special', labelKey: 'ach.cat.special' },
  { key: 'milestone', labelKey: 'ach.cat.milestone' },
];

interface AchRow {
  container: PIXI.Container;
  gfx: PIXI.Graphics;
  iconText: PIXI.Text;
  nameText: PIXI.Text;
  descText: PIXI.Text;
  def: AchievementDef;
}

export class AchievementPanel extends PIXI.Container {
  private scroll: ScrollContainer;
  private panelW: number;

  // Category filter
  private filterContainer = new PIXI.Container();
  private filterChips: { gfx: PIXI.Graphics; text: PIXI.Text; key: AchCategory }[] = [];
  private activeFilter: AchCategory = 'all';

  // Progress text
  private progressText!: PIXI.Text;

  // Achievement rows
  private achRows: AchRow[] = [];

  constructor(w: number, h: number, _core: unknown) {
    super();
    this.panelW = w;

    // Progress text at the very top
    this.progressText = makeText('0/55', 14, THEME.text.gold, { fontWeight: 'bold' });
    this.progressText.anchor.set(1, 0);
    this.addChild(this.progressText);

    // Filter chips (not scrollable)
    this.addChild(this.filterContainer);
    this.buildFilterChips();

    // Scrollable area below filters
    const filterH = 50;
    this.scroll = new ScrollContainer(w, h - filterH);
    this.scroll.y = filterH;
    this.addChild(this.scroll);

    this.buildAchievementRows();
  }

  private buildFilterChips(): void {
    let x = LAYOUT.padding;
    const chipH = 28;

    for (const cat of CATEGORIES) {
      const label = cat.key === 'all' ? '\u2B50' : t(cat.labelKey);
      const chipW = Math.max(label.length * 10 + 16, 44);

      const gfx = new PIXI.Graphics();
      gfx.eventMode = 'static';
      gfx.cursor = 'pointer';
      gfx.on('pointerdown', () => this.setFilter(cat.key));
      this.filterContainer.addChild(gfx);

      const txt = makeText(label, 11, THEME.text.primary, { fontWeight: 'bold' });
      txt.anchor.set(0.5, 0.5);
      txt.x = x + chipW / 2;
      txt.y = 14;
      this.filterContainer.addChild(txt);

      this.filterChips.push({ gfx, text: txt, key: cat.key });

      // Draw initial chip
      this.drawChip(gfx, x, 0, chipW, chipH, cat.key === this.activeFilter);

      x += chipW + 6;
    }
  }

  private drawChip(gfx: PIXI.Graphics, x: number, y: number, w: number, h: number, active: boolean): void {
    gfx.clear();
    if (active) {
      gfx.beginFill(THEME.accent.gold, 0.2);
      gfx.lineStyle(1, THEME.accent.gold, 0.5);
    } else {
      gfx.beginFill(THEME.bg.glass, THEME.bg.glassAlpha);
      gfx.lineStyle(1, THEME.border.glass, THEME.border.glassAlpha);
    }
    gfx.drawRoundedRect(x, y, w, h, h / 2);
    gfx.endFill();
    gfx.lineStyle(0);
  }

  private setFilter(key: AchCategory): void {
    this.activeFilter = key;

    // Re-draw all chips
    let x = LAYOUT.padding;
    const chipH = 28;
    for (const chip of this.filterChips) {
      const label = chip.key === 'all' ? '\u2B50' : t(CATEGORIES.find(c => c.key === chip.key)!.labelKey);
      const chipW = Math.max(label.length * 10 + 16, 44);
      this.drawChip(chip.gfx, x, 0, chipW, chipH, chip.key === this.activeFilter);
      chip.text.x = x + chipW / 2;
      chip.text.style.fill = chip.key === this.activeFilter ? THEME.text.gold : THEME.text.secondary;
      x += chipW + 6;
    }

    this.scroll.resetScroll();
  }

  private buildAchievementRows(): void {
    const content = this.scroll.content;

    for (const def of ACHIEVEMENTS) {
      const container = new PIXI.Container();

      const gfx = new PIXI.Graphics();
      container.addChild(gfx);

      const iconText = makeText('', 22, THEME.text.white);
      iconText.anchor.set(0.5);
      container.addChild(iconText);

      const nameText = makeText('', 13, THEME.text.primary, { fontWeight: 'bold' });
      container.addChild(nameText);

      const descText = makeText('', 11, THEME.text.secondary);
      container.addChild(descText);

      content.addChild(container);
      this.achRows.push({ container, gfx, iconText, nameText, descText, def });
    }
  }

  refresh(state: GameState): void {
    const pad = LAYOUT.padding;
    const rowW = this.panelW - pad * 2;
    const rowH = LAYOUT.rowH;

    // Progress
    const totalUnlocked = state.unlockedAchievements.length;
    this.progressText.text = `${totalUnlocked}/${ACHIEVEMENTS.length}`;
    this.progressText.x = this.panelW - pad;
    this.progressText.y = 4;

    // Filter and layout rows
    let y = pad;

    for (const row of this.achRows) {
      const matchesFilter = this.activeFilter === 'all' || row.def.category === this.activeFilter;
      row.container.visible = matchesFilter;
      if (!matchesFilter) continue;

      const unlocked = state.unlockedAchievements.includes(row.def.id);

      row.container.y = y;

      // Draw card
      row.gfx.clear();
      if (unlocked) {
        drawGlassCard(row.gfx, pad, 0, rowW, rowH, LAYOUT.cardRadius, 0.10);
      } else {
        drawGlassCard(row.gfx, pad, 0, rowW, rowH, LAYOUT.cardRadius, 0.04);
      }

      // Icon
      row.iconText.text = unlocked ? '\u2705' : '\uD83D\uDD12';
      row.iconText.x = pad + 30;
      row.iconText.y = rowH / 2;

      // Name
      row.nameText.text = t(row.def.nameKey);
      row.nameText.x = pad + 58;
      row.nameText.y = rowH / 2 - 12;
      row.nameText.style.fill = unlocked ? THEME.text.primary : THEME.text.secondary;

      // Desc
      row.descText.text = t(row.def.descKey);
      row.descText.x = pad + 58;
      row.descText.y = rowH / 2 + 6;
      row.descText.style.fill = unlocked ? THEME.text.secondary : THEME.accent.dimWhite;

      y += rowH + LAYOUT.gap;
    }

    y += pad;
    this.scroll.setContentHeight(y);
  }

  onResize(w: number, h: number): void {
    this.panelW = w;
    const filterH = 50;
    this.scroll.resize(w, h - filterH);
  }

  updateScroll(dt: number): void {
    this.scroll.update(dt);
  }
}
